import { getPortfolios, updatePortfolio } from "@/pages/PortfolioBuilder/api";
import { tagsByTerm } from "./tagsForTerms";

/**
 * Attach tags to already-saved watchlists, out of band.
 *
 * Tags are an enhancement, not a prerequisite: `transformSearchParams` sends a
 * list's tags alongside its tickers and keywords under filter_mode OR, so a
 * tagged list matches precisely on tag-carrying feeds while an untagged one is
 * still matched on text. Nothing about the list is broken while they are
 * missing.
 *
 * They are also the expensive part of saving one. Resolving a term is a
 * fan-out of tag lookups, so a twenty-ticker list — "top 10 pharma and top 10
 * financials" is exactly that — spent several seconds on lookups before the
 * first record was written, with the button sitting on "Saving…" throughout.
 * It read as a hang because there was nothing else to read it as.
 *
 * So the records are written first and this runs afterwards, unawaited. It
 * re-reads each watchlist before patching rather than trusting what the caller
 * saved, so a list the user has meanwhile edited keeps their edit and only
 * gains the tags.
 *
 * Never rejects: this is fire-and-forget, and an unhandled rejection here
 * would be an error toast for work the user has already been told succeeded.
 */
export function backfillWatchlistTags(
  ids: number[],
  onSettled?: () => void
): void {
  if (ids.length === 0) return;
  void (async () => {
    try {
      const wanted = new Set(ids);
      const live = (await getPortfolios()).filter((p) => wanted.has(p.id));
      if (live.length === 0) return;

      // One pass over every term in every list: `resolveTerms` dedupes, so
      // overlapping lists get cheaper rather than more expensive.
      const tagByTerm = await tagsByTerm(
        live.flatMap((p) => [...p.tickers, ...(p.keywords ?? [])])
      );

      for (const portfolio of live) {
        /**
         * Derived from the list's current terms, replacing what was there
         * rather than adding to it. Every path that writes tags derives them
         * this way, so a union would leave the tag of a ticker the user has
         * just deleted attached to the list for good.
         */
        const tags = [
          ...new Set(
            [...portfolio.tickers, ...(portfolio.keywords ?? [])]
              .map((term) => tagByTerm.get(term.trim()))
              .filter((id): id is string => !!id)
          ),
        ];
        const current = portfolio.tags ?? [];
        const unchanged =
          tags.length === current.length && tags.every((t) => current.includes(t));
        if (unchanged) continue;
        await updatePortfolio({ ...portfolio, tags });
      }
    } catch {
      // The lists are saved and usable; they simply match on text for now.
    } finally {
      onSettled?.();
    }
  })();
}
